import { useEffect, useRef, useState } from 'react';
import { Filter, ChevronsRight } from 'lucide-react';
import type { QuarterMode, TabId, TabState } from '../types';
import { SUB_REGIONS } from '../utils/regions';
import { getFYOptions, isAllSub, isAllQ, isAllFY, getQuarterMode, quarterModeLabel, switchQuarterMode, getFilterSummary, CLOSED_MODE_STATUSES } from '../utils/filter';
import { hasDateRange } from '../utils/dateRange';
import { useRequests } from '../context/RequestsContext';
import { track } from '../analytics';
interface Props {
  tab: TabId;
  state: TabState & {
    status?: string;
  };
  onUpdate: (updates: Partial<TabState>) => void;
}

// Analytics: filter values here are categorical (region, FY, quarter, status, quarter mode) -- never free text.
const FILTER_KEYS = {
  r: 'region',
  sub: 'sub_region',
  status: 'status',
  fy: 'fiscal_year',
  q: 'quarter',
  qMode: 'quarter_mode'
} as const;
function trackFilterChanges(tab: TabId, updates: Partial<TabState>) {
  (Object.keys(FILTER_KEYS) as (keyof typeof FILTER_KEYS)[]).forEach(key => {
    const v = updates[key];
    if (v === undefined) return;
    track('filter.change', {
      tab,
      filter: FILTER_KEYS[key],
      value: Array.isArray(v) ? v.join(',') : String(v)
    });
  });
}
export function FilterBar({
  tab,
  state,
  onUpdate: onUpdateRaw
}: Props) {
  const {
    requests
  } = useRequests();
  const onUpdate = (updates: Partial<TabState>) => {
    //trackFilterChanges(tab, updates);
    onUpdateRaw(updates);
  };
  const qMode = getQuarterMode(state);
  const modeLabel = quarterModeLabel(qMode);
  const closedMode = qMode === 'closed';
  const fyOptions = getFYOptions(requests, qMode).filter(y => y !== 'All');
  const [fyOpen, setFyOpen] = useState(false);
  const fyRef = useRef<HTMLDivElement>(null);

  // Close the FY dropdown on outside click or Escape.
  useEffect(() => {
    if (!fyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (fyRef.current && !fyRef.current.contains(e.target as Node)) setFyOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFyOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [fyOpen]);

  // Button text: "All FY", "FY2026", "FY2025, FY2026", or "FY2024 +2".
  const fySelected = (state.fy ?? []).filter(y => y !== 'All');
  const fyButtonLabel = isAllFY(state.fy) ? 'All FY' : fySelected.length <= 2 ? fySelected.map(y => 'FY' + y).join(', ') : `FY${fySelected[0]} +${fySelected.length - 1}`;
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const statuses = ['All', 'In Progress', 'Closed', 'On Hold', 'Cancelled'];
  const regions = ['Global', 'AMER', 'EMEA', 'JAPAC'];
  const subRegions = SUB_REGIONS[state.r] ?? [];
  function setRegion(r: string) {
    // On region change, reset sub-region selection to the whole region ("All").
    onUpdate({
      r,
      sub: ['All']
    });
  }

  // Toggle a single sub-region on/off. Selecting any sub clears the "All"
  // sentinel; deselecting the last selected sub snaps back to ['All'].
  function toggleSub(s: string) {
    const cur = (state.sub ?? []).filter(x => x !== 'All');
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    onUpdate({
      sub: next.length ? next : ['All']
    });
  }

  // Toggle a single fiscal year on/off. Same "All" sentinel behaviour as quarters.
  function toggleFY(y: string) {
    const cur = (state.fy ?? []).filter(x => x !== 'All');
    const next = cur.includes(y) ? cur.filter(x => x !== y) : [...cur, y];
    const ordered = fyOptions.filter(x => next.includes(x));
    onUpdate({
      fy: ordered.length ? ordered : ['All'],
      ...CLEAR_RANGE
    });
  }

  // Toggle a single quarter on/off. Selecting any quarter clears the "All"
  // sentinel; deselecting the last selected quarter snaps back to ['All'].
  function toggleQ(q: string) {
    const cur = (state.q ?? []).filter(x => x !== 'All');
    const next = cur.includes(q) ? cur.filter(x => x !== q) : [...cur, q];
    const ordered = quarters.filter(x => next.includes(x));
    onUpdate({
      q: ordered.length ? ordered : ['All'],
      ...CLEAR_RANGE
    });
  }

  // Quarter filter and Date Range are mutually exclusive: using one resets the other.
  const CLEAR_RANGE = {
    dateFrom: '',
    dateTo: ''
  };
  const rangeActive = hasDateRange(state);

  // Created ↔ Closed quarter mode (ADR 0010). The date range is kept and reinterpreted.
  function setQuarterMode(next: QuarterMode) {
    if (next === qMode) return;
    onUpdate(switchQuarterMode(state, next, requests));
  }
  function setDateEnd(end: 'dateFrom' | 'dateTo', value: string) {
    const other = end === 'dateFrom' ? state.dateTo : state.dateFrom;
    const updates: Partial<TabState> = {
      [end]: value,
      fy: ['All'],
      q: ['All']
    };
    // Keep the range well-ordered: dragging one end past the other pulls the other along.
    if (value && other && (end === 'dateFrom' ? value > other : value < other)) {
      updates[end === 'dateFrom' ? 'dateTo' : 'dateFrom'] = value;
    }
    onUpdate(updates);
  }
  function clearRange() {
    onUpdate({
      ...CLEAR_RANGE,
      fy: ['All'],
      q: ['All']
    });
  }
  return <div data-track-ignore className="bg-white px-[18px] py-[10px] flex gap-4 flex-wrap items-start border-b border-[#e8eaf0] shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-140" data-mp="1v089pbdt3rfb">
      {/* Region */}
      <div className="flex flex-col gap-[6px]" data-id="element-141" data-mp="29qpb4uqfyhc7">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-142" data-mp="24oq7rkupbkf9">Region</div>
        <div className="flex gap-1 flex-wrap" data-id="element-143" data-mp="2ioj17nxm4eqx">
          {regions.map(r => <button key={r} className={`rpill ${state.r === r ? 'active' : ''}`} onClick={() => setRegion(r)} data-id="element-144" data-mp="2enfm53ne2mqr">
              {r}
            </button>)}
        </div>
        {subRegions.length > 0 && <div className="flex items-center gap-[6px] pt-1 flex-wrap" data-id="element-145" data-mp="kaznanwxaavx">
            <span className="text-[9px] font-semibold text-[#8892a0] uppercase tracking-[.05em] whitespace-nowrap flex items-center gap-1" data-id="element-146" data-mp="2jey13j8u8a2n">
              <ChevronsRight className="w-[10px] h-[10px] text-[#d0d5dd]" data-id="element-147" data-mp="2oekgnzc522em" />Sub-region
            </span>
            <div className="flex gap-1 flex-wrap" data-id="element-148" data-mp="2tbatmdsnchvt">
              <button className={`srpill ${isAllSub(state.sub) ? 'active' : ''}`} onClick={() => onUpdate({
            sub: ['All']
          })} data-id="element-149" data-mp="2ybp4s4pjo7y2">
                All {state.r}
              </button>
              {subRegions.map(s => <button key={s} className={`srpill ${state.sub.includes(s) ? 'active' : ''}`} onClick={() => toggleSub(s)} data-id="element-150" data-mp="37ak9fccxx9z9">
                  {s}
                </button>)}
            </div>
          </div>}
      </div>

      {/* Status (VP only) */}
      {tab === 'vp' && <div className="flex flex-col gap-[6px]" data-id="element-151" data-mp="3q3xw6u6i2a3s">
          <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-152" data-mp="1eft41ico70wd">Status</div>
          <div className="flex gap-1 flex-wrap" data-id="element-153" data-mp="1ae7iz9pzfgb3">
            {statuses.map(s => {
          const disabled = closedMode && !(CLOSED_MODE_STATUSES as readonly string[]).includes(s);
          return <button key={s} className={`rpill ${(state.status ?? 'All') === s ? 'active' : ''} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`} disabled={disabled} title={disabled ? 'Closed Quarter mode shows closed requests only' : undefined} onClick={() => onUpdate({
            status: s
          })} data-id="element-154" data-mp="10gjmofcqa0qt">
                  {s}
                </button>;
        })}
          </div>
        </div>}

      {/* Created / Closed Quarter */}
      <div className="flex flex-col gap-[6px]" data-id="element-155" data-mp="2gvkuxn17fys3">
        <div className="flex items-center gap-[6px]" data-id="element-156" data-mp="2c31javm2vdpx">
          <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-157" data-mp="2c28gg5q8p8d2">{modeLabel} Quarter</div>
          <button type="button" role="switch" aria-checked={closedMode} aria-label="Filter by closed quarter" title={closedMode ? 'Switch to created quarter' : 'Switch to closed quarter'} onClick={() => setQuarterMode(closedMode ? 'created' : 'closed')} className={`relative inline-flex h-[12px] w-[22px] flex-shrink-0 items-center rounded-full transition-colors ${closedMode ? 'bg-[#15803D]' : 'bg-[#d0d5dd]'}`} data-id="element-158" data-mp="1ke5gjd1uox40">
            <span className={`inline-block h-[8px] w-[8px] rounded-full bg-white shadow transition-transform ${closedMode ? 'translate-x-[12px]' : 'translate-x-[2px]'}`} data-id="element-159" data-mp="1u5s1i13zqajy" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-160" data-mp="iqd44w7vdgob">
          <div ref={fyRef} className="relative" data-id="element-161" data-mp="isza4rf43o4f">
            <button type="button" className="fy-select" style={{
            textAlign: 'left',
            minWidth: 96,
            whiteSpace: 'nowrap'
          }} aria-haspopup="listbox" aria-expanded={fyOpen} aria-label={`${modeLabel} fiscal year`} onClick={() => setFyOpen(o => !o)} data-id="element-162" data-mp="nqr6pyqpbyey">
              {fyButtonLabel}
            </button>
            {fyOpen && <div role="listbox" aria-multiselectable="true" className="absolute left-0 top-[30px] z-30 min-w-[130px] bg-white border border-[#d0d5dd] rounded-[6px] shadow-[0_4px_12px_rgba(0,0,0,0.12)] py-1" data-id="element-163" data-mp="k5p9so5mmeva">
                <label className="flex items-center gap-2 px-3 py-[5px] text-[11px] text-[#344054] cursor-pointer hover:bg-[#f5f7fa]" data-id="element-164" data-mp="68sk4q18v3fz">
                  <input type="checkbox" checked={isAllFY(state.fy)} onChange={() => onUpdate({
                fy: ['All'],
                ...CLEAR_RANGE
              })} data-id="element-165" data-mp="1fhc4lkp4gmh" />
                  All FY
                </label>
                <div className="h-px bg-[#e8eaf0] my-1" data-id="element-166" data-mp="2c8ztbdynmpcx" />
                {fyOptions.map(y => <label key={y} className="flex items-center gap-2 px-3 py-[5px] text-[11px] text-[#344054] cursor-pointer hover:bg-[#f5f7fa]" data-id="element-167" data-mp="27cj5kkvdad0d">
                    <input type="checkbox" checked={state.fy.includes(y)} onChange={() => toggleFY(y)} data-id="element-168" data-mp="1xc8t52h5p9m9" />
                    {'FY' + y}
                  </label>)}
              </div>}
          </div>
          <div className="w-px h-[18px] bg-[#e8eaf0] flex-shrink-0" data-id="element-169" data-mp="1jlwwqrdjgopb" />
          <button className={`cqfpill ${isAllQ(state.q) ? 'active' : ''}`} onClick={() => onUpdate({
          q: ['All'],
          ...CLEAR_RANGE
        })} data-id="element-170" data-mp="1enlnahr5og7m">
            All Q
          </button>
          {quarters.map(q => <button key={q} className={`cqfpill ${state.q.includes(q) ? 'active' : ''}`} onClick={() => toggleQ(q)} data-id="element-171" data-mp="15nywepw1ysst">
              {q}
            </button>)}
        </div>
      </div>

      {/* Date Range -- on the entry date (Created) or completion date (Closed); mutually exclusive with the quarter filter */}
      <div className="flex flex-col gap-[6px]" data-id="element-172" data-mp="3v7nulwkiipq8">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-173" data-mp="2xl8d4us35s2o">{modeLabel} Date Range</div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-174" data-mp="32js4xbtd1y2y">
          <input type="date" className="dr-input" aria-label={`${modeLabel} from`} value={state.dateFrom ?? ''} max={state.dateTo || undefined} onChange={e => setDateEnd('dateFrom', e.target.value)} data-id="element-175" data-mp="32i8onl7pfn16" />
          <span className="text-[10px] text-[#8892a0]" data-id="element-176" data-mp="2eugybdpbx9gw">–</span>
          <input type="date" className="dr-input" aria-label={`${modeLabel} to`} value={state.dateTo ?? ''} min={state.dateFrom || undefined} onChange={e => setDateEnd('dateTo', e.target.value)} data-id="element-177" data-mp="2jt1yxe8htv5v" />
          {rangeActive && <button className="dr-clear" onClick={clearRange} data-id="element-178" data-mp="2lhw2anmu1lb6">Clear</button>}
        </div>
      </div>

      {/* Summary label */}
      <div className="text-[10px] text-[#8892a0] ml-auto self-center flex items-center gap-1" data-id="element-179" data-mp="2glxgn7sw2heg">
        <Filter className="w-[11px] h-[11px]" data-id="element-180" data-mp="1swcmrtv75zqe" />
        {getFilterSummary(tab, state)}
      </div>
    </div>;
}